import React, { useState, cloneElement } from 'react';
import { ChevronDownIcon, ChevronUpIcon, TrophyIcon, AwardIcon, FlagIcon, TrendingUpIcon, ArrowRightIcon } from 'lucide-react';
type CareerEvent = {
  id: string;
  year: string;
  type: 'team_change' | 'award' | 'championship' | 'record' | 'transfer' | 'international';
  title: string;
  description: string;
  fromTeam?: {
    name: string;
    logo: string;
    league: string;
  };
  toTeam?: {
    name: string;
    logo: string;
    league: string;
    position: string;
  };
  icon?: React.ReactNode;
  color: string;
  details?: string;
};
const PlayerCareer = () => {
  const [expandedEvents, setExpandedEvents] = useState<string[]>([]);
  const toggleEvent = (eventId: string) => {
    if (expandedEvents.includes(eventId)) {
      setExpandedEvents(expandedEvents.filter(id => id !== eventId));
    } else {
      setExpandedEvents([...expandedEvents, eventId]);
    }
  };
  const careerEvents: CareerEvent[] = [{
    id: '1',
    year: '2023',
    type: 'team_change',
    title: 'Signed with Los Angeles Lakers',
    description: 'Signed a 3-year, $90 million contract with the Los Angeles Lakers',
    fromTeam: {
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      league: 'NBA'
    },
    toTeam: {
      name: 'Los Angeles Lakers',
      logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
      league: 'NBA',
      position: 'Point Guard'
    },
    icon: <ArrowRightIcon data-id="element-33" />,
    color: 'bg-purple-100 border-purple-300',
    details: 'Joined the Lakers as part of a major rebuild following their disappointing playoff exit. Expected to form a dynamic backcourt duo with Austin Reaves.'
  }, {
    id: '2',
    year: '2022',
    type: 'award',
    title: 'NBA All-Star Selection',
    description: 'Selected to the NBA All-Star team for the first time',
    icon: <AwardIcon data-id="element-34" />,
    color: 'bg-yellow-100 border-yellow-300',
    details: 'First All-Star selection after averaging 24.3 points, 6.8 assists, and 4.2 rebounds per game in the first half of the season.'
  }, {
    id: '3',
    year: '2022',
    type: 'record',
    title: 'Career-high 52 points',
    description: 'Scored a career-high 52 points against the New York Knicks',
    icon: <TrendingUpIcon data-id="element-35" />,
    color: 'bg-green-100 border-green-300',
    details: 'Shot 18-of-29 from the field, including 8-of-13 from three-point range, and added 7 assists in a 128-115 victory at Madison Square Garden.'
  }, {
    id: '4',
    year: '2021',
    type: 'international',
    title: 'Olympic Gold Medal',
    description: 'Won gold medal with USA Basketball at Tokyo Olympics',
    icon: <FlagIcon data-id="element-36" />,
    color: 'bg-blue-100 border-blue-300',
    details: 'Averaged 12.8 points and 4.6 assists coming off the bench for Team USA. Hit the game-winning shot in the semifinal against Australia.'
  }, {
    id: '5',
    year: '2020',
    type: 'championship',
    title: 'NBA Championship',
    description: 'Won the NBA Championship with the Brooklyn Nets',
    icon: <TrophyIcon data-id="element-37" />,
    color: 'bg-red-100 border-red-300',
    details: 'Played a crucial role as the third option, averaging 18.4 points and 5.2 assists during the playoff run. Hit a clutch three-pointer in Game 7 of the Eastern Conference Finals.'
  }, {
    id: '6',
    year: '2019',
    type: 'transfer',
    title: 'Traded to Brooklyn Nets',
    description: 'Traded from Boston Celtics to Brooklyn Nets in blockbuster deal',
    fromTeam: {
      name: 'Boston Celtics',
      logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
      league: 'NBA'
    },
    toTeam: {
      name: 'Brooklyn Nets',
      logo: 'https://cdn.nba.com/logos/nba/1610612751/global/L/logo.svg',
      league: 'NBA',
      position: 'Point Guard / Shooting Guard'
    },
    icon: <ArrowRightIcon data-id="element-38" />,
    color: 'bg-purple-100 border-purple-300',
    details: 'Part of a multi-player trade package that sent two first-round picks to Boston. Brooklyn sought to add scoring and playmaking to complement their existing stars.'
  }, {
    id: '7',
    year: '2017',
    type: 'team_change',
    title: 'Signed with Boston Celtics',
    description: 'Signed a 4-year rookie contract with the Boston Celtics',
    toTeam: {
      name: 'Boston Celtics',
      logo: 'https://cdn.nba.com/logos/nba/1610612738/global/L/logo.svg',
      league: 'NBA',
      position: 'Point Guard'
    },
    icon: <ArrowRightIcon data-id="element-39" />,
    color: 'bg-purple-100 border-purple-300',
    details: 'Selected 3rd overall in the 2017 NBA Draft by the Boston Celtics. Considered one of the most NBA-ready players in the draft class.'
  }, {
    id: '8',
    year: '2016',
    type: 'award',
    title: 'NCAA Player of the Year',
    description: 'Named NCAA Player of the Year after outstanding college season',
    icon: <AwardIcon data-id="element-40" />,
    color: 'bg-yellow-100 border-yellow-300',
    details: 'Led his team to the Final Four while averaging 23.2 points, 5.7 assists, and 3.8 rebounds per game. Shot 47.2% from three-point range.'
  }, {
    id: '9',
    year: '2015',
    type: 'team_change',
    title: 'Joined Duke University',
    description: 'Began college career at Duke University',
    toTeam: {
      name: 'Duke Blue Devils',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e1/Duke_Athletics_logo.svg',
      league: 'NCAA',
      position: 'Point Guard'
    },
    icon: <ArrowRightIcon data-id="element-41" />,
    color: 'bg-purple-100 border-purple-300',
    details: 'Five-star recruit who chose Duke over offers from Kentucky, Kansas, and North Carolina. Immediately named a starter by Coach K.'
  }];
  return <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-42">
      <h3 className="font-bold text-xl mb-6" data-id="element-43">CAREER OVERVIEW</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-44">
        {/* Biography Section */}
        <div className="space-y-6" data-id="element-45">
          <div data-id="element-46">
            <h4 className="font-bold text-lg text-gray-800 mb-3 border-b pb-1" data-id="element-47">
              Professional Story
            </h4>
            <div className="text-sm text-gray-700 space-y-2" data-id="element-48">
              <p data-id="element-49">
                Born with an innate talent for basketball, his journey to NBA
                stardom began in the suburbs of Chicago, where he dominated
                local youth leagues. By high school, he had established himself
                as a five-star recruit, drawing attention from top college
                programs nationwide.
              </p>
              <p data-id="element-50">
                At Duke University, he quickly became the team's cornerstone,
                leading the Blue Devils to the Final Four while earning NCAA
                Player of the Year honors. His exceptional court vision, scoring
                ability, and leadership qualities made him a top prospect in the
                2017 NBA Draft.
              </p>
              <p data-id="element-51">
                Selected 3rd overall by the Boston Celtics, he made an immediate
                impact in the league. After two promising seasons in Boston, he
                was traded to the Brooklyn Nets in a blockbuster deal that
                shocked the basketball world. In Brooklyn, he flourished
                alongside established stars, helping the team capture an NBA
                Championship in 2020.
              </p>
              <p data-id="element-52">
                His international career has been equally impressive,
                representing Team USA at the Tokyo Olympics and bringing home a
                gold medal. Now with the Los Angeles Lakers, he continues to
                evolve his game while mentoring younger players and pursuing
                additional championships.
              </p>
            </div>
          </div>
          <div data-id="element-53">
            <h4 className="font-bold text-lg text-gray-800 mb-3 border-b pb-1" data-id="element-54">
              Personal Story
            </h4>
            <div className="text-sm text-gray-700 space-y-2" data-id="element-55">
              <p data-id="element-56">
                Off the court, he maintains a relatively private life despite
                his growing fame. Raised in a close-knit family with three
                siblings, he credits his parents for instilling the discipline
                and work ethic that fueled his success. His father, a former
                college basketball player, introduced him to the game at age
                four.
              </p>
              <p data-id="element-57">
                A dedicated philanthropist, he established a foundation that
                focuses on youth education and basketball development in
                underserved communities. Each summer, he hosts basketball camps
                across the country, personally mentoring promising young talent.
              </p>
              <p data-id="element-58">
                He's known for his diverse interests beyond basketball. An avid
                reader with a particular interest in history and biographies,
                he's often spotted with books during team travels. He's also
                developed a passion for wine collecting and has invested in
                several tech startups.
              </p>
              <p data-id="element-59">
                Despite his hectic schedule, he prioritizes family time and is
                known to fly relatives to important games. He and his wife, a
                pediatrician he met during college, are raising two young
                children away from the spotlight, emphasizing normalcy despite
                his celebrity status.
              </p>
            </div>
          </div>
        </div>
        {/* Timeline Section - More Compact */}
        <div className="relative" data-id="element-60">
          <h4 className="font-bold text-lg text-gray-800 mb-3 border-b pb-1" data-id="element-61">
            Career Timeline
          </h4>
          {/* Timeline line */}
          <div className="absolute left-12 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-62"></div>
          {/* Timeline events */}
          <div className="space-y-3" data-id="element-63">
            {careerEvents.map(event => <div key={event.id} className="relative" data-id="element-64">
                {/* Year marker */}
                <div className="absolute left-0 top-0 w-10 h-10 flex items-center justify-center" data-id="element-65">
                  <div className="text-xs font-bold text-gray-600" data-id="element-66">
                    {event.year}
                  </div>
                </div>
                {/* Event marker */}
                <div className="absolute left-10 top-2 w-4 h-4 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center z-10" data-id="element-67">
                  <div className={`w-2 h-2 rounded-full ${event.type === 'championship' ? 'bg-red-500' : event.type === 'award' ? 'bg-yellow-500' : event.type === 'international' ? 'bg-blue-500' : event.type === 'record' ? 'bg-green-500' : 'bg-purple-500'}`} data-id="element-68"></div>
                </div>
                {/* Event content */}
                <div className={`ml-16 p-2 rounded-lg border text-sm ${event.color}`} data-id="element-69">
                  <div className="flex justify-between items-start" data-id="element-70">
                    <div data-id="element-71">
                      <h4 className="font-bold text-gray-800 text-xs" data-id="element-72">
                        {event.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-0.5" data-id="element-73">
                        {event.description}
                      </p>
                      {/* Team transition for team changes and transfers */}
                      {(event.type === 'team_change' || event.type === 'transfer') && <div className="flex items-center mt-1 space-x-1" data-id="element-74">
                          {event.fromTeam && <div className="flex items-center" data-id="element-75">
                              <img src={event.fromTeam.logo} alt={event.fromTeam.name} className="w-6 h-6 object-contain" data-id="element-76" />
                              <div className="ml-1" data-id="element-77">
                                <div className="text-xs font-medium" data-id="element-78">
                                  {event.fromTeam.name}
                                </div>
                              </div>
                            </div>}
                          {event.fromTeam && <ArrowRightIcon className="w-3 h-3 text-gray-400" data-id="element-79" />}
                          {event.toTeam && <div className="flex items-center" data-id="element-80">
                              <img src={event.toTeam.logo} alt={event.toTeam.name} className="w-6 h-6 object-contain" data-id="element-81" />
                              <div className="ml-1" data-id="element-82">
                                <div className="text-xs font-medium" data-id="element-83">
                                  {event.toTeam.name}
                                </div>
                              </div>
                            </div>}
                        </div>}
                      {/* Icons for awards, championships, etc. */}
                      {(event.type === 'award' || event.type === 'championship' || event.type === 'record' || event.type === 'international') && <div className="mt-1 flex items-center" data-id="element-84">
                          <span className={`inline-flex items-center justify-center p-0.5 rounded-full 
                            ${event.type === 'championship' ? 'bg-red-500 text-white' : event.type === 'award' ? 'bg-yellow-500 text-white' : event.type === 'international' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`} data-id="element-85">
                            {cloneElement(event.icon as React.ReactElement, {
                        className: 'w-3 h-3'
                      })}
                          </span>
                        </div>}
                    </div>
                    {/* Expand/collapse button */}
                    <button onClick={() => toggleEvent(event.id)} className="text-gray-500 hover:text-gray-700" data-id="element-86">
                      {expandedEvents.includes(event.id) ? <ChevronUpIcon className="w-4 h-4" data-id="element-87" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-88" />}
                    </button>
                  </div>
                  {/* Expanded details */}
                  {expandedEvents.includes(event.id) && <div className="mt-2 pt-1 border-t border-gray-200" data-id="element-89">
                      <p className="text-xs text-gray-700" data-id="element-90">{event.details}</p>
                    </div>}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default PlayerCareer;